/**
 * P-37 (D-194) — "אני על זה" / "גמרתי", the composer's "טיימר מענה" and the
 * short lines that fade away. Pure, unit-tested
 * (`src/utils/__tests__/replyTimerModel.test.ts`).
 */

/** Under one minute nothing is counted (Q-176) — the same rule as the server. */
export const MIN_REPLY_SECONDS = 60;
/** T-63 / D-198 QX-145 — the reply box counts at most 120 minutes per message (the server caps it too). */
export const MAX_REPLY_BOX_MINUTES = 120;

/** QX-145 — true when the reply box's time was cut at the cap (the employee is told by a toast). */
export function replyBoxCapped(seconds: number | undefined | null): boolean {
  return typeof seconds === 'number' && seconds > MAX_REPLY_BOX_MINUTES * 60;
}

/** How long a fading line stays (Q-180 / Q-182). */
export const FADING_LINE_MS = 10_000;

/** Q-172 — "אני על זה" / "יצירת משימה מהקריאה": staff, on an open SERVICE TICKET only (tasks do not change). */
export function showReplyActions(o: { isStaff: boolean; taskType?: string | null; status?: string | null; cancelled?: boolean }): boolean {
  if (!o.isStaff || o.taskType !== 'ticket' || o.cancelled) return false;
  return o.status !== 'completed' && o.status !== 'closed';
}

/**
 * Q-178 + P-45b (D-210, QX-215) — the composer's "טיימר מענה" box: on a ticket
 * it starts checked; on a CONVERSATION with a customer it starts checked; on an
 * internal conversation and on a discussion it starts unchecked (only offered).
 * Never on a task.
 */
export function replyBoxDefault(o?: string | null | { taskType?: string | null; isConversation?: boolean; hasCustomer?: boolean }): boolean {
  if (typeof o === 'string' || o == null) return o === 'ticket';
  if (o.taskType === 'ticket') return true;
  return o.taskType === 'discussion' && o.isConversation === true && o.hasCustomer === true;
}

/**
 * Whether the box is shown: staff, a ticket or a discussion, a message to the
 * customer / participants (not an internal note), and NOT while "אני על זה"
 * runs on this ticket (Q-175 — that timer already counts the time).
 */
export function showReplyBox(o: { isStaff: boolean; taskType?: string | null; internalMode: boolean; replyTimerRunning: boolean; employeesOnlyChat?: boolean }): boolean {
  // FIX-D326-3 - a 1:1 chat between employees has no customer to answer: no "טיימר מענה" at all.
  if (!o.isStaff || o.internalMode || o.replyTimerRunning || o.employeesOnlyChat) return false;
  return o.taskType === 'ticket' || o.taskType === 'discussion';
}

/**
 * The seconds sent with the message: from the first sign of a reply (typing,
 * recording, a file — Q-174) to the send. Nothing when the box is off, the
 * reply never started, or it took under a minute (the server decides too).
 */
export function replySecondsToSend(o: { checked: boolean; startedAt: number | null; now: number }): number | undefined {
  if (!o.checked || o.startedAt === null) return undefined;
  const secs = Math.max(0, Math.round((o.now - o.startedAt) / 1000));
  return secs >= MIN_REPLY_SECONDS ? secs : undefined;
}

/** "גמרתי" — the optional mark next to it (Q-173). No mark: the ticket stays "בטיפול". */
export type DoneMark = 'none' | 'completed' | 'waiting_customer';

/** The status the ticket moves to after "גמרתי", or null (stays as it is). */
export function statusAfterDone(mark: DoneMark): 'completed' | 'waiting_customer' | null {
  return mark === 'none' ? null : mark;
}

/** The fading line after a send / a start. */
export type ReplyNotice =
  | { kind: 'sent'; minutes: number }
  /** FIX-P37 — the message went out but its reply time could not be put on any engagement. */
  | { kind: 'unlogged'; minutes: number }
  | { kind: 'paused'; replyTimerId: string; titles: string[] };

/** Q-180 — after a send that stopped "אני על זה": the line, or null when nothing was stopped. */
export function sentNotice(replyTimer: { stopped?: boolean; minutes?: number } | null | undefined): ReplyNotice | null {
  if (!replyTimer?.stopped) return null;
  return { kind: 'sent', minutes: Math.max(0, Math.round(Number(replyTimer.minutes) || 0)) };
}

/** Q-182 — after "אני על זה" paused a running task timer: the line, or null. */
export function pausedNotice(replyTimerId: string | null | undefined, paused: Array<{ ticket_title: string | null }> | null | undefined): ReplyNotice | null {
  if (!replyTimerId || !paused || paused.length === 0) return null;
  return { kind: 'paused', replyTimerId, titles: paused.map((p) => p.ticket_title || '').filter(Boolean) };
}

/**
 * FIX-P37 — what the composer does with the server's `reply_log` of a sent
 * message: nothing (logged / no log), ask for an engagement once, or say that
 * the time was not recorded (a customer with no engagement at all).
 */
export type ReplyLogAction =
  | { kind: 'none' }
  | { kind: 'ask'; seconds: number; minutes: number; engagements: Array<{ id: string; type: string; name: string | null }> }
  | { kind: 'unlogged'; minutes: number };

export function replyLogAction(log: { logged?: boolean; reason?: string; minutes?: number; seconds?: number; engagements?: unknown } | null | undefined): ReplyLogAction {
  if (!log || log.logged !== false) return { kind: 'none' };
  const minutes = Math.max(1, Math.round(Number(log.minutes) || 0));
  if (log.reason === 'engagement_required' && Array.isArray(log.engagements) && log.engagements.length > 0 && Number(log.seconds) > 0) {
    return { kind: 'ask', seconds: Number(log.seconds), minutes, engagements: log.engagements as any };
  }
  return { kind: 'unlogged', minutes };
}
